import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { Option } from '../Select/Select';
import { useWarehouses } from '../../../services/warehouse';
import { findWarehouseOption, mapWarehousesToOptions } from './helpers/warehouseHelpers';

type WarehouseSelectProps = {
  defaultWarehouse?: number;
  defaultWarehouseName?: string;
  onChange: (warehouseId: number | undefined) => void;
  disabled?: boolean;
  className?: string;
};

const WarehouseSelect: React.FC<WarehouseSelectProps> = ({
  defaultWarehouse,
  defaultWarehouseName,
  onChange,
  disabled,
  className,
}) => {
  const { warehouses, isFetching, isLoading } = useWarehouses();

  const { t } = useTranslation();

  const warehouseOptions: Option[] = useMemo(() => {
    const options = mapWarehousesToOptions(warehouses);
    if (!defaultWarehouse || options.some(option => option.value === String(defaultWarehouse))) {
      return options;
    }
    return [...options, { label: defaultWarehouseName || String(defaultWarehouse), value: String(defaultWarehouse) }];
  }, [warehouses, defaultWarehouse, defaultWarehouseName]);

  const selectedWarehouse = useMemo(() => {
    return findWarehouseOption(warehouseOptions, defaultWarehouse);
  }, [warehouseOptions, defaultWarehouse]);

  return (
    !isFetching && (
      <Select
        options={warehouseOptions}
        defaultValue={selectedWarehouse}
        onChange={option => {
          if (option) {
            const warehouseId = parseInt(option.value, 10);
            onChange(Number.isNaN(warehouseId) ? undefined : warehouseId);
          } else {
            onChange(undefined);
          }
        }}
        placeholder={t('outbound.select_warehouse')}
        disabled={disabled || isLoading}
        className={className}
        isSearchable
      />
    )
  );
};

export default WarehouseSelect;
